import React, { createContext, useContext, useState, useEffect, useRef, useCallback } from 'react';
import { saveProgressToS3, onSyncStatusChange, getSyncStatus } from '../services/gameProgressSync';
import { useUser } from './UserContext';
import { checkAchievements, getAchievementProgress } from '../utils/achievementsEngine';
import { getJourneyPersona, generateJourneySequence } from '../utils/journeyEngine';
import { CURATED_JOURNEY_MAP } from '../utils/curatedJourneys';
import levelsData from '../game/levels.json';

const GameContext = createContext();

export const useGame = () => {
  const context = useContext(GameContext);
  if (!context) {
    throw new Error('useGame must be used within a GameProvider');
  }
  return context;
};

// LocalStorage keys
const STORAGE_KEYS = {
  PROGRESS: 'evcs_game_progress',
  ACHIEVEMENTS: 'evcs_achievements',
  STATS: 'evcs_stats',
};

export const GameProvider = ({ children }) => {
  console.log('[BOOT] GameProvider: mounting');
  const { user } = useUser(); // Get current logged-in user
  const syncUnsubscribeRef = useRef(null);
  const _pendingAchievementsRef = useRef([]); // bridge between setProgress updater and toast state

  // Sync state
  const [syncStatus, setSyncStatus] = useState(getSyncStatus());

  // Load progress from localStorage
  const loadProgress = () => {
    try {
      const stored = localStorage.getItem(STORAGE_KEYS.PROGRESS);
      if (stored) {
        const parsed = JSON.parse(stored);
        // Migrate old data to new format if needed
        if (!parsed.levelHistory) parsed.levelHistory = {};
        if (!parsed.streakData) parsed.streakData = { currentStreak: 0, longestStreak: 0, lastPlayedDate: null };
        if (!parsed.totalAttempts) parsed.totalAttempts = 0;
        if (!parsed.totalPlayTime) parsed.totalPlayTime = 0; // in seconds
        if (!parsed.createdAt) parsed.createdAt = new Date().toISOString();
        if (!parsed.unlockedAchievements) parsed.unlockedAchievements = [];
        if (!parsed.journeyPreferences) parsed.journeyPreferences = [];
        if (!parsed.curatedJourneyId) parsed.curatedJourneyId = null;
        if (parsed.journeyPreferences.length > 0 && !parsed.journeyPersona) {
          parsed.journeyPersona = getJourneyPersona(parsed.journeyPreferences);
        } else if (!parsed.journeyPersona) {
          parsed.journeyPersona = null;
        }
        // Migrate: regenerate journeySequence if missing but preferences exist
        if (!parsed.journeySequence || parsed.journeySequence.length === 0) {
          if (parsed.curatedJourneyId && CURATED_JOURNEY_MAP[parsed.curatedJourneyId]) {
            parsed.journeySequence = CURATED_JOURNEY_MAP[parsed.curatedJourneyId].levelIds;
          } else if (parsed.journeyPreferences && parsed.journeyPreferences.length > 0) {
            parsed.journeySequence = generateJourneySequence(
              parsed.journeyPreferences,
              levelsData.levels
            ).map((l) => l.id);
          } else {
            parsed.journeySequence = [];
          }
        }
        return parsed;
      }
    } catch (error) {
      console.error('Error loading progress:', error);
    }
    // Default: only level 1 unlocked
    return {
      unlockedLevels: [1],
      completedLevels: [],
      levelStars: {}, // { levelId: numberOfStars }
      highScores: {}, // { levelId: percentage }
      levelHistory: {}, // { levelId: [{ score, stars, timestamp, noteAccuracy, duration }] }
      streakData: { currentStreak: 0, longestStreak: 0, lastPlayedDate: null },
      totalAttempts: 0,
      totalPlayTime: 0, // total seconds played
      createdAt: new Date().toISOString(),
      unlockedAchievements: [], // [{ id, unlockedAt }]
      journeyPreferences: [],   // string[] of selected tag IDs
      curatedJourneyId: null,   // 'foundation' | 'ascending' | 'descending' | null
      journeyPersona: null,     // { name, icon, desc, color }
      journeySequence: [],      // number[] ordered level IDs for this journey
    };
  };

  const [progress, setProgress] = useState(loadProgress());
  const [currentLevel, setCurrentLevel] = useState(null);
  // Queued achievement toasts: array of achievement IDs waiting to be shown
  const [pendingAchievementToasts, setPendingAchievementToasts] = useState([]);

  // Retroactive achievement check: run once on mount so that any progress
  // already stored (or merged from S3) immediately grants earned achievements.
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    const alreadyUnlocked = (progress.unlockedAchievements || []).map((a) => a.id);
    const newIds = checkAchievements(progress, alreadyUnlocked);
    if (newIds.length > 0) {
      const now = new Date().toISOString();
      setProgress((prev) => ({
        ...prev,
        unlockedAchievements: [
          ...(prev.unlockedAchievements || []),
          ...newIds.map((id) => ({ id, unlockedAt: now })),
        ],
      }));
      // Don't toast retroactive unlocks — silently grant them
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // run once on mount

  // Subscribe to sync status changes
  useEffect(() => {
    syncUnsubscribeRef.current = onSyncStatusChange((status) => {
      setSyncStatus(status);
    });

    return () => {
      if (syncUnsubscribeRef.current) {
        syncUnsubscribeRef.current();
      }
    };
  }, []);

  // Save progress to localStorage whenever it changes
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(progress));
      console.log('📝 Progress saved to localStorage:', progress);
    } catch (error) {
      console.error('Error saving progress:', error);
    }

    // SYNC TO S3: Queue sync if user is logged in
    if (user?.sub) {
      console.log('🔄 Triggering S3 sync for user:', user.sub, user.email);
      saveProgressToS3(user.sub, user.email, progress);
    } else {
      console.log('⚠️ No user logged in, skipping S3 sync');
    }
  }, [progress, user]);

  // Check if a level is unlocked
  const isLevelUnlocked = (levelId) => {
    return progress.unlockedLevels.includes(levelId);
  };

  // Get stars for a level
  const getLevelStars = (levelId) => {
    return progress.levelStars[levelId] || 0;
  };

  // Get high score for a level
  const getHighScore = (levelId) => {
    return progress.highScores[levelId] || 0;
  };

  // Complete a level and update progress
  const completeLevel = (levelId, stars, score, noteAccuracy = null, duration = null) => {
    console.log(`🎮 completeLevel called: level=${levelId}, stars=${stars}, score=${score}`);
    
    const now = new Date();
    const timestamp = now.toISOString();
    
    setProgress((prev) => {
      const newProgress = { ...prev };

      // Update total attempts
      newProgress.totalAttempts = (newProgress.totalAttempts || 0) + 1;
      
      // Update total play time (duration in seconds)
      if (duration) {
        newProgress.totalPlayTime = (newProgress.totalPlayTime || 0) + duration;
      }

      // Update level history (keep last 50 attempts per level for performance)
      if (!newProgress.levelHistory) newProgress.levelHistory = {};
      if (!newProgress.levelHistory[levelId]) newProgress.levelHistory[levelId] = [];
      
      newProgress.levelHistory[levelId].push({
        score,
        stars,
        timestamp,
        noteAccuracy: noteAccuracy || { perfect: 0, good: 0, missed: 0 },
        duration: duration || 0,
      });
      
      // Keep only last 50 attempts per level
      if (newProgress.levelHistory[levelId].length > 50) {
        newProgress.levelHistory[levelId] = newProgress.levelHistory[levelId].slice(-50);
      }

      // Update streak data
      if (!newProgress.streakData) {
        newProgress.streakData = { currentStreak: 0, longestStreak: 0, lastPlayedDate: null };
      }
      
      const lastPlayed = newProgress.streakData.lastPlayedDate;
      const today = now.toISOString().split('T')[0]; // YYYY-MM-DD
      
      if (lastPlayed) {
        const lastPlayedDate = new Date(lastPlayed);
        const lastPlayedDay = lastPlayedDate.toISOString().split('T')[0];
        const daysDiff = Math.floor((now - lastPlayedDate) / (1000 * 60 * 60 * 24));
        
        if (lastPlayedDay === today) {
          // Same day, no streak change
        } else if (daysDiff === 1) {
          // Consecutive day, increase streak
          newProgress.streakData.currentStreak += 1;
          newProgress.streakData.lastPlayedDate = timestamp;
        } else {
          // Streak broken, reset
          newProgress.streakData.currentStreak = 1;
          newProgress.streakData.lastPlayedDate = timestamp;
        }
      } else {
        // First time playing
        newProgress.streakData.currentStreak = 1;
        newProgress.streakData.lastPlayedDate = timestamp;
      }
      
      // Update longest streak
      if (newProgress.streakData.currentStreak > newProgress.streakData.longestStreak) {
        newProgress.streakData.longestStreak = newProgress.streakData.currentStreak;
      }

      // Update completed levels
      if (!newProgress.completedLevels.includes(levelId)) {
        newProgress.completedLevels.push(levelId);
      }

      // Update stars (keep best)
      const currentStars = newProgress.levelStars[levelId] || 0;
      if (stars > currentStars) {
        newProgress.levelStars[levelId] = stars;
      }

      // Update high score (keep best)
      const currentScore = newProgress.highScores[levelId] || 0;
      if (score > currentScore) {
        newProgress.highScores[levelId] = score;
      }

      // Unlock next level in the journey sequence if got 3+ stars
      if (stars >= 3) {
        const journeyIds = newProgress.journeySequence || [];
        const pos = journeyIds.indexOf(levelId);
        const nextId = pos >= 0 && pos < journeyIds.length - 1 ? journeyIds[pos + 1] : null;
        if (nextId && !newProgress.unlockedLevels.includes(nextId)) {
          newProgress.unlockedLevels.push(nextId);
        }
        // Fallback: also unlock levelId+1 if journey not set (no sequence)
        if (journeyIds.length === 0) {
          const fallbackNext = levelId + 1;
          if (!newProgress.unlockedLevels.includes(fallbackNext)) {
            newProgress.unlockedLevels.push(fallbackNext);
          }
        }
      }

      // ── Achievement check ──────────────────────────────────────────────
      const alreadyUnlocked = (newProgress.unlockedAchievements || []).map((a) => a.id);
      const newlyUnlocked = checkAchievements(newProgress, alreadyUnlocked);
      if (newlyUnlocked.length > 0) {
        const now2 = new Date().toISOString();
        newProgress.unlockedAchievements = [
          ...(newProgress.unlockedAchievements || []),
          ...newlyUnlocked.map((id) => ({ id, unlockedAt: now2 })),
        ];
        // Queue toasts — set state outside the updater via a ref-based flush
        _pendingAchievementsRef.current = [
          ...(_pendingAchievementsRef.current || []),
          ...newlyUnlocked,
        ];
      }
      // ──────────────────────────────────────────────────────────────────

      console.log('📊 New progress state:', newProgress);
      return newProgress;
    });

    // Flush queued achievement IDs into toast state after setProgress
    // Use setTimeout so it runs after the updater has committed
    setTimeout(() => {
      if (_pendingAchievementsRef.current?.length) {
        setPendingAchievementToasts((prev) => [...prev, ..._pendingAchievementsRef.current]);
        _pendingAchievementsRef.current = [];
      }
    }, 0);
  };

  // Dismiss a toast once it has been shown
  const dismissAchievementToast = useCallback((id) => {
    setPendingAchievementToasts((prev) => prev.filter((aid) => aid !== id));
  }, []);

  // Set a curated journey (Foundation / Ascending / Descending).
  // Replaces any tag-based journey.
  const setCuratedJourney = useCallback((journeyId) => {
    const curated = CURATED_JOURNEY_MAP[journeyId];
    if (!curated) return;
    const sequenceIds = curated.levelIds;
    const firstId = sequenceIds[0] || 1;
    setProgress((prev) => ({
      ...prev,
      curatedJourneyId: journeyId,
      journeyPreferences: [],
      journeyPersona: {
        name: curated.name,
        icon: curated.emoji,
        desc: curated.description,
        color: curated.color,
      },
      journeySequence: sequenceIds,
      unlockedLevels: Array.from(new Set([firstId, ...prev.unlockedLevels])),
    }));
  }, []);

  // Save journey preferences and regenerate the 50-level journey sequence.
  // Special case: tags === ['beta-tester'] unlocks ALL 100 levels immediately.
  // Special case: tags === ['curated:<id>'] selects a curated journey.
  const setJourneyPreferences = useCallback((tags) => {
    // Handle curated journey selection (from BubbleCloud curated cards)
    if (tags.length === 1 && tags[0].startsWith('curated:')) {
      const journeyId = tags[0].replace('curated:', '');
      setCuratedJourney(journeyId);
      return;
    }
    if (tags.length === 1 && tags[0] === 'beta-tester') {
      const allIds = levelsData.levels.map((l) => l.id);
      setProgress((prev) => ({
        ...prev,
        journeyPreferences: ['beta-tester'],
        journeyPersona: {
          name: 'Beta Tester',
          icon: '🔬',
          desc: 'All 100 levels unlocked for testing.',
          color: '#ff6b35',
        },
        journeySequence: allIds,
        unlockedLevels: allIds,
      }));
      return;
    }
    const persona = getJourneyPersona(tags);
    const sequenceLevels = generateJourneySequence(tags, levelsData.levels);
    const sequenceIds = sequenceLevels.map((l) => l.id);
    // Unlock only the first level in the new journey
    const firstId = sequenceIds[0] || 1;
    setProgress((prev) => ({
      ...prev,
      curatedJourneyId: null,
      journeyPreferences: tags,
      journeyPersona: persona,
      journeySequence: sequenceIds,
      // Reset unlocked to just the first journey level (keeps completed intact)
      unlockedLevels: Array.from(new Set([firstId, ...prev.unlockedLevels])),
    }));
  }, []);

  // Reset all progress (for testing)
  const resetProgress = () => {
    const defaultProgress = {
      unlockedLevels: [1],
      completedLevels: [],
      levelStars: {},
      highScores: {},
      levelHistory: {},
      streakData: { currentStreak: 0, longestStreak: 0, lastPlayedDate: null },
      totalAttempts: 0,
      totalPlayTime: 0,
      createdAt: new Date().toISOString(),
      unlockedAchievements: [],
    };
    setProgress(defaultProgress);
    localStorage.setItem(STORAGE_KEYS.PROGRESS, JSON.stringify(defaultProgress));
  };

  // Get total stats
  const getStats = () => {
    const totalLevelsCompleted = progress.completedLevels.length;
    const totalStars = Object.values(progress.levelStars).reduce((sum, stars) => sum + stars, 0);
    const averageScore = progress.completedLevels.length > 0
      ? Object.values(progress.highScores).reduce((sum, score) => sum + score, 0) / progress.completedLevels.length
      : 0;

    return {
      totalLevelsCompleted,
      totalStars,
      averageScore: Math.round(averageScore),
      maxPossibleStars: progress.completedLevels.length * 5,
      totalAttempts: progress.totalAttempts || 0,
      totalPlayTime: progress.totalPlayTime || 0,
      currentStreak: progress.streakData?.currentStreak || 0,
      longestStreak: progress.streakData?.longestStreak || 0,
    };
  };

  // Get level attempts count
  const getLevelAttempts = (levelId) => {
    return progress.levelHistory?.[levelId]?.length || 0;
  };

  // Get level history
  const getLevelHistory = (levelId) => {
    return progress.levelHistory?.[levelId] || [];
  };

  // Get all history (for charts)
  const getAllHistory = () => {
    return progress.levelHistory || {};
  };

  const value = {
    progress,
    currentLevel,
    setCurrentLevel,
    isLevelUnlocked,
    getLevelStars,
    getHighScore,
    completeLevel,
    resetProgress,
    getStats,
    getLevelAttempts,
    getLevelHistory,
    getAllHistory,
    unlockedLevels: progress.unlockedLevels,
    completedLevels: progress.completedLevels,
    // Achievements
    unlockedAchievements: progress.unlockedAchievements || [],
    pendingAchievementToasts,
    dismissAchievementToast,
    getAchievementProgress: () => getAchievementProgress(progress),
    // Journey
    journeyPreferences: progress.journeyPreferences || [],
    curatedJourneyId: progress.curatedJourneyId || null,
    journeyPersona: progress.journeyPersona || null,
    setCuratedJourney,
    setJourneyPreferences,
    // Journey sequence as full level objects (50-level ordered list)
    journeySequence: (progress.journeySequence || []).map(
      (id) => levelsData.levels.find((l) => l.id === id)
    ).filter(Boolean),
    journeySequenceIds: progress.journeySequence || [],
    // Sync state
    syncStatus,
    setProgress, // Expose for UserContext to update after merge
  };

  return <GameContext.Provider value={value}>{children}</GameContext.Provider>;
};
